import { ColumnDef, flexRender, getCoreRowModel, OnChangeFn, SortingState, useReactTable } from '@tanstack/react-table';
import {
  ButtonLink,
  cn,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Pagination,
  RiMore2Line,
  RiSearchLine,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
} from 'component-library';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Organization } from '@/api/rest/resources/types/organization';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useDebounce } from '@/hooks/useDebounce';
import { useOrganization } from '@/hooks/useOrganization';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useSetOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { ToggleSortButton } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/plot-list/ToggleSortButton';
import { AddPlotsModal } from '@/pages/shared/staff-portal/components/AddPlotsModal';
import { paths } from '@/routing';
import { buildPath } from '@/utils/buildPath';

const PAGE_SIZE = 20;

export const OrganizationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const setOrganizationId = useSetOrganizationId();
  const [uploadOrgId, setUploadOrgId] = useState<string | null>(null);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const sortParam = searchParams.get('sort') || '';
  const [searchInput, setSearchInput] = useState(searchParams.get('query') || '');
  const debouncedSearch = useDebounce(searchInput, 400);

  const initialSorting = useMemo<SortingState>(() => {
    if (!sortParam) return [];
    const desc = sortParam.startsWith('-');
    return [{ id: desc ? sortParam.slice(1) : sortParam, desc }];
  }, [sortParam]);

  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(next);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        const first = next[0];
        if (first) {
          params.set('sort', `${first.desc ? '-' : ''}${first.id}`);
        } else {
          params.delete('sort');
        }
        params.set('page', '1');
        return params;
      },
      { preventScrollReset: true },
    );
  };

  const { data } = useOrganization({
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    sort: sortParam || undefined,
    query: debouncedSearch || undefined,
  });

  const { count: totalCount, results: organizations } = data;

  const columns = useMemo<ColumnDef<Organization>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <ToggleSortButton column={column}>{t('global.organization.name', 'Name')}</ToggleSortButton>
        ),
        cell: ({ row }) => (
          <Stack direction='row' centerMain className='gap-3'>
            <div className='w-8 h-8 bg-neutral-black-4 items-center justify-center flex shrink-0'>
              <Icon icon='ri-hotel-line' className='text-base' />
            </div>
            <span className='typography-body1 font-medium'>{row.original.name}</span>
          </Stack>
        ),
      },
      {
        accessorKey: 'plots_count',
        header: ({ column }) => (
          <ToggleSortButton column={column}>{t('global.organization.plotsCount', 'Plots')}</ToggleSortButton>
        ),
        cell: ({ row }) => <span className='typography-body1'>{row.original.plots_count}</span>,
      },
      {
        accessorKey: 'modified_at',
        header: ({ column }) => (
          <ToggleSortButton column={column}>{t('global.lastEdited', 'Last Edited')}</ToggleSortButton>
        ),
        cell: ({ row }) => (
          <span className='typography-body1'>
            {row.original.modified_at ? new Date(row.original.modified_at).toLocaleDateString() : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'actions',
        header: () => null,
        cell: ({ row }) => (
          <OrganizationRowActions organization={row.original} onUploadPlot={(org) => setUploadOrgId(org.id)} />
        ),
      },
    ],
    [t, setUploadOrgId],
  );

  const table = useReactTable({
    data: organizations,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    onSortingChange,
    state: { sorting },
  });

  const onRowClick = (org: Organization) => {
    setOrganizationId(org.id);
    navigate(buildPath(paths.staff.plots, { queryParams: { organizationId: org.id } }), {
      state: { previousPath: pathname },
    });
  };

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value) {
          params.set('query', value);
        } else {
          params.delete('query');
        }
        params.set('page', '1');
        return params;
      },
      { preventScrollReset: true },
    );
  };

  return (
    <>
      <SignedInPageLayout
        items={[]}
        withCustomContentContainer
        showBreadcrumbs={false}
        navBarProps={{
          title: t('global.organization.organizations'),
          showSearchBar: true,
          customSearchBar: <SearchBar value={searchInput} onChange={onSearchChange} />,
          showFilters: false,
          children: (
            <ButtonLink
              to={buildPath(paths.staff.globalCopilot)}
              variant='outline'
              leftAdornment={<Icon icon='ri-record-circle-line' />}
            >
              {t('global.copilot.title')}
            </ButtonLink>
          ),
        }}
      >
        <Stack className='px-48 py-12 gap-6'>
          <span className='typography-overline text-text-secondary'>{`${totalCount} ${t('global.organization.organizations')}`}</span>
          <div className='rounded-2xl bg-white-100 border border-neutral-black-12'>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className='typography-overline h-12 px-6 py-4 text-left align-middle leading-4 text-text-secondary'
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className='border-b border-divider hover:bg-neutral-black-4 hover:cursor-pointer'
                    onClick={() => onRowClick(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          'typography-body1 p-6 align-middle',
                          cell.column.id === 'actions' && 'w-px py-3 pr-4',
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            className='typography-overline text-text-secondary mx-auto'
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={(nextPage) => {
              setSearchParams(
                (prev) => {
                  const params = new URLSearchParams(prev);
                  params.set('page', `${nextPage}`);
                  return params;
                },
                { preventScrollReset: true },
              );
            }}
          />
        </Stack>
      </SignedInPageLayout>
      {uploadOrgId && (
        <AddPlotsModal
          open={!!uploadOrgId}
          onOpenChange={(open) => !open && setUploadOrgId(null)}
          organizationId={uploadOrgId}
        />
      )}
    </>
  );
};

const OrganizationRowActions = ({
  organization,
  onUploadPlot,
}: {
  organization: Organization;
  onUploadPlot: (org: Organization) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Dropdown modal={false}>
      <DropdownTrigger data-testid='org-row-overflow-menu' onClick={(e) => e.stopPropagation()}>
        <div className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary-100 text-primary-100 hover:bg-neutral-black-4 cursor-pointer'>
          <RiMore2Line size={24} />
        </div>
      </DropdownTrigger>
      <DropdownContent side='bottom' sideOffset={10}>
        <DropdownItem
          data-testid='org-action-upload-plot'
          onClick={(e) => {
            e.stopPropagation();
            onUploadPlot(organization);
          }}
          leftAdornment={<Icon icon='ri-file-upload-line' />}
        >
          {t('shared.organizations.actions.uploadPlot', 'Upload Plot')}
        </DropdownItem>
        <DropdownItem
          data-testid='org-action-ndi'
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              buildPath(paths.staff.organizationNdiSubscriptions, {
                pathParams: { organizationId: organization.id },
              }),
            );
          }}
          leftAdornment={<Icon icon='ri-database-2-line' />}
        >
          {t('shared.organizations.actions.ndi', 'NDI')}
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
};

const SearchBar = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const { t } = useTranslation();

  return (
    <TextInput
      data-testid='navbar-search-input'
      placeholder={t('shared.searchBar.placeholder')}
      variant='standard'
      value={value}
      className='flex-1 typography-body1'
      StartIcon={<RiSearchLine className='text-primary-100' />}
      size='small'
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        disableUnderline: true,
        style: {
          borderRadius: '999px',
          backgroundColor: '#004C3D14',
          padding: '4px 16px',
        },
        sx: {
          '& input': {
            paddingTop: '4px',
          },
        },
      }}
    />
  );
};
